import { PcsTranslateService } from '@app/core/translate/translate.service';
import {
  DisplaySettingsColumn,
  DisplaySettingsFilter,
  DisplaySettingsSort,
  GridDisplaySettings
} from '@app/models';
import { DossierSearchItem, GridType } from '@app/pcs/models';
import { reorderColumns } from '@app/pcs/util';
import {
  getDisplaySettingsField,
  getDisplaySettingsFilterOption,
  getDisplaySettingsSortType
} from '@app/pcs/util/grid/mappings';
import { Action } from '@ngrx/store';
import { isNotNil } from '@util';
import { DataGridColumn, updateGridData } from 'ngrx-data-grid';
import * as R from 'ramda';

export const setColumnVisibility = R.curry((columns: DisplaySettingsColumn[], {
  field,
  visible
}: DataGridColumn): Partial<DataGridColumn> => {
  const column = R.find<DisplaySettingsColumn>(R.propEq('field', getDisplaySettingsField(field)), columns);

  return {
    visible: column ? !column.hidden : visible
  };
});

export const setColumnFilter = R.curry((filters: DisplaySettingsFilter[], column: DataGridColumn): Partial<DataGridColumn> => {
  const filter = R.find<DisplaySettingsFilter>(R.propEq('field', getDisplaySettingsField(column.field)), filters);

  return filter && column.filter
    ? ({
      filter: {
        ...column.filter,
        option: getDisplaySettingsFilterOption(filter.operator),
        value: filter.value
      }
    })
    : column;
});

export const setColumnSort = R.curry((sorts: DisplaySettingsSort[], column: DataGridColumn): Partial<DataGridColumn> => {
  const sort = R.find<DisplaySettingsSort>(R.propEq('field', getDisplaySettingsField(column.field)), sorts);

  return sort && column.sortAvailable ? ({
    sortAvailable: true,
    sortType: getDisplaySettingsSortType(sort.dir)
  })
    : column;
});

export const setColumnWidth = R.curry((columns: DisplaySettingsColumn[], column: DataGridColumn): Partial<DataGridColumn> => {
  const columnSettings = R.find<DisplaySettingsColumn>(R.propEq('field', getDisplaySettingsField(column.field)), columns);

  return columnSettings && columnSettings?.width
    ? R.mergeRight(column, {width: columnSettings.width})
    : column;
});

type UpdateColumn = (column: DataGridColumn) => Partial<DataGridColumn>;
export const updateColumn = R.curry((update: UpdateColumn, column: DataGridColumn): DataGridColumn => ({
  ...column,
  ...update(column)
}));

type GetGridColumnsConfig = (gridDisplaySettings: GridDisplaySettings) => DisplaySettingsColumn[];
export const getGridColumnsConfig: GetGridColumnsConfig = R.propOr([], 'columns');

type GetGridFilters = (gridDisplaySettings: GridDisplaySettings) => DisplaySettingsFilter[];
export const getGridFilters: GetGridFilters = R.pathOr([], ['gridFilter', 'filters']);

type GetGridSort = (gridDisplaySettings: GridDisplaySettings) => DisplaySettingsSort[];
export const getGridSort: GetGridSort = R.propOr([], 'gridSorting');

type ContainsDossierId = (dossierIds: number[]) => (gridElement: DossierSearchItem) => boolean;
const containsDossierId: ContainsDossierId = R.curry((dossierIds: number[], gridElement: DossierSearchItem) => R.includes(R.prop('dossierId', gridElement), dossierIds));

export const updateGridElements = (shouldUpdate: (gridElement: any) => boolean, update: (gridElement: any) => any): Action =>
  updateGridData({
    name: GridType.Search,
    shouldUpdate,
    update
  });

export const updateGridDataForSelection = (dossierIds: number[], update: (gridElement: any) => any): Action =>
  updateGridElements(containsDossierId(dossierIds), update);

export const columnFactory = (t: PcsTranslateService) =>
  ({headerName, field, visible, ...rest}: Partial<DataGridColumn>) =>
    (props: Partial<DataGridColumn> = {}) => ({
      headerName: t.translate(headerName),
      field,
      visible,
      ...rest,
      ...props
    }) as DataGridColumn;

export const getGridColumns = (columns: DataGridColumn[], displaySettings: GridDisplaySettings): DataGridColumn[] => {
  const reorderedColumns = reorderColumns(displaySettings?.columnsOrder, columns);
  const columnsConfig: DisplaySettingsColumn[] = getGridColumnsConfig(displaySettings);
  const filters: DisplaySettingsFilter[] = getGridFilters(displaySettings);
  const sorts: DisplaySettingsSort[] = getGridSort(displaySettings);

  const updateColumnProperties = R.compose(
    updateColumn(setColumnVisibility(columnsConfig)),
    updateColumn(setColumnFilter(filters)),
    updateColumn(setColumnSort(sorts)),
    updateColumn(setColumnWidth(columnsConfig))
  );

  return R.map(updateColumnProperties, reorderedColumns);
};

export const getGridColumnsFromConfig = (columns: DataGridColumn[], configuration: string): DataGridColumn[] =>
  getGridColumns(columns, isNotNil(configuration) ? JSON.parse(configuration) : {});
